import React, { useState, useEffect } from 'react';
import { LevelService } from '../../../services/LevelService';
import { SavedMapInfo } from '../../../services/LevelStorageService';

/**
 * EditorLoadOverlay - A gallery view for loading maps into the editor.
 * Fetches both local and API levels.
 */

interface EditorLoadOverlayProps {
  isVisible: boolean;
  onLoad: (mapId: string) => void;
  onCancel: () => void;
}

export const EditorLoadOverlay: React.FC<EditorLoadOverlayProps> = ({
  isVisible,
  onLoad,
  onCancel
}) => {
  const [maps, setMaps] = useState<SavedMapInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible) {
      refreshMaps();
    }
  }, [isVisible]);

  const refreshMaps = async () => {
    setLoading(true);
    const availableMaps = await LevelService.getInstance().fetchAvailableMaps();
    setMaps(availableMaps);
    setLoading(false);
  };

  if (!isVisible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>LOAD MAP</h2>
          <button style={styles.closeBtn} onClick={onCancel}>✕</button>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>Loading available maps...</div>
          ) : (
            <div style={styles.grid}>
              {maps.map(map => (
                <div
                  key={map.id}
                  style={{
                    ...styles.mapCard,
                    borderColor: selectedId === map.id ? '#3498db' : '#333',
                    backgroundColor: selectedId === map.id ? 'rgba(52, 152, 219, 0.1)' : 'rgba(255, 255, 255, 0.05)'
                  }}
                  onClick={() => setSelectedId(map.id)}
                  onDoubleClick={() => onLoad(map.id)}
                >
                  <div style={styles.mapIcon}>
                    {map.isBuiltIn ? '🏰' : '📄'}
                  </div>
                  <div style={styles.mapInfo}>
                    <div style={styles.mapName}>{map.name}</div>
                    <div style={styles.mapMeta}>
                      {map.isBuiltIn ? 'Built-in Map' : 'Custom Level'} • {map.id}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onCancel}>CANCEL</button>
          <button
            style={{
              ...styles.loadBtn,
              opacity: selectedId ? 1 : 0.5,
              cursor: selectedId ? 'pointer' : 'not-allowed'
            }}
            disabled={!selectedId}
            onClick={() => selectedId && onLoad(selectedId)}
          >
            LOAD SELECTED
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    backdropFilter: 'blur(5px)',
    pointerEvents: 'auto' // Ensure overlay receives clicks
  },
  container: {
    width: '600px',
    height: '500px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
  },
  header: {
    padding: '15px 20px',
    borderBottom: '1px solid #333',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#222'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#fff',
    letterSpacing: '1px'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#666',
    fontSize: '20px',
    cursor: 'pointer'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px'
  },
  loading: {
    textAlign: 'center',
    color: '#888',
    marginTop: '100px'
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  mapCard: {
    padding: '12px 15px',
    border: '1px solid #333',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  mapIcon: {
    fontSize: '24px'
  },
  mapInfo: {
    flex: 1
  },
  mapName: {
    fontSize: '16px',
    color: '#fff',
    fontWeight: 'bold'
  },
  mapMeta: {
    fontSize: '11px',
    color: '#777',
    marginTop: '2px'
  },
  footer: {
    padding: '15px 20px',
    borderTop: '1px solid #333',
    background: '#222',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  cancelBtn: {
    padding: '10px 20px',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  loadBtn: {
    padding: '10px 25px',
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 'bold'
  }
};
